import React, { useEffect, useState } from "react";
import axios from "axios";
import "../styles/CyclingSummary.css";
import calculateSpeedAndPace from "../../utils/calculateSpeedAndPace";
import { calculateTSSWithTerrainCycling } from "../../utils/tssCyclingUtils";
import { calculateNormalizedPower } from "../../utils/normalizedPower";
import { calculateVO2Max } from "../../utils/vo2Utils";
import { useTranslation } from "../../i18n";
import API_BASE_URL from '../../config/api';

const CyclingSummary = ({ fileId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  const safe = (val, digits = 2) =>
    val !== undefined && val !== null && !isNaN(val) ? Number(val).toFixed(digits) : "-";

  const resolveFtp = (payload) => {
    const v = Number(
      payload?.ftp ??
        payload?.athlete?.ftp ??
        payload?.user?.ftp ??
        payload?.profile?.ftp ??
        payload?.athleteProfile?.ftp ??
        payload?.zones_target?.functional_threshold_power ??
        0
    );
    return Number.isFinite(v) && v > 0 ? v : 0;
  };

  useEffect(() => {
    const fetchCyclingData = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
        const payload  = res.data || {};
        const session  = Array.isArray(payload.session) ? payload.session[0] : payload.session;
        const zones    = payload.zones_target || {};
        const activity = payload.activity || {};
        const completed = payload.completed || {};
        const records  = Array.isArray(payload.records) ? payload.records : [];

        const { avgSpeed } = calculateSpeedAndPace(payload);

        const maxSpeedFromRecords = records.length
          ? Math.max(...records.map((record) => (record.speed ?? 0) * 3.6))
          : 0;
        const maxSpeed = maxSpeedFromRecords > 0 ? maxSpeedFromRecords.toFixed(2) : "0.00";

        const normalizedPower = calculateNormalizedPower(records);
        const ftp = resolveFtp(payload);
        const tssObj = calculateTSSWithTerrainCycling(payload);
        const tss = (!tssObj?.tss || isNaN(tssObj?.tss)) ? "0.00" : tssObj.tss;

        const ifFromUtil = tssObj?.intensityFactor ?? tssObj?.IF ?? tssObj?.if ?? null;
        const rawIF =
          ifFromUtil ??
          completed?.intensityFactor ??
          (ftp > 0 && Number(normalizedPower) > 0 ? Number(normalizedPower) / ftp : null);

        const weight   = payload.athlete?.weight || 70;
        const avgPower = completed?.avgPower ?? session?.avg_power ?? 0;
        const wKg      = avgPower && weight ? avgPower / weight : null;

        let vo2 = payload.vo2max ?? session?.vo2max_estimated;
        if (!vo2 && session?.total_distance && session?.total_timer_time) {
          vo2 = calculateVO2Max(session.total_distance / 1000, session.total_timer_time);
        }

        setData({
          date:          activity?.timestamp ? new Date(activity.timestamp).toLocaleString() : "-",
          sport:         `${session?.sport || ""}${session?.sub_sport ? " - " + session.sub_sport : ""}`,
          duration:      session?.total_timer_time ? new Date(session.total_timer_time * 1000).toISOString().substr(11, 8) : "-",
          distance:      safe((session?.total_distance ?? 0) / 1000),
          calories:      session?.total_calories ?? "-",
          ascent:        session?.total_ascent ?? "-",
          descent:       session?.total_descent ?? "-",
          temperatureAvg: session?.avg_temperature ?? "-",
          temperatureMax: session?.max_temperature ?? "-",
          tss:           safe(tss),
          vo2max:        safe(vo2),
          ftp:           ftp || "-",
          thr:           zones.threshold_heart_rate ?? "-",
          avgHR:         session?.avg_heart_rate ?? "-",
          maxHR:         session?.max_heart_rate ?? "-",
          avgSpeed,
          maxSpeed,
          avgCadence:    session?.avg_cadence ?? "-",
          maxCadence:    session?.max_cadence ?? "-",
          avgPower,
          maxPower:      completed?.maxPower ?? session?.max_power ?? "-",
          normalizedPower: safe(normalizedPower),
          intensityFactor: safe(rawIF),
          wKg:           safe(wKg),
          totalWork:     completed?.totalWork ? safe(completed.totalWork / 1000) : '-',
        });
      } catch (err) {
        console.error("Error al cargar los datos de ciclismo:", err);
      }
    };

    if (fileId) fetchCyclingData();
  }, [fileId]);

  if (!data) return null;

  return (
    <div className="summary-container">
      <h4 className="mb-3" style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('cycling.summaryTitle')}</h4>
      <div className="summary-grid">
        <div className="summary-item">
          <strong><i className="bi bi-calendar-event"></i> {t('cycling.date')}</strong>
          <p>{data.date}</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-bicycle"></i> {t('cycling.sport')}</strong>
          <p>{data.sport}</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-clock-history"></i> {t('cycling.durationLabel')}</strong>
          <p>{data.duration}</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-rulers"></i> {t('cycling.distanceLabel')}</strong>
          <p>{data.distance} km</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-fire"></i> {t('cycling.caloriesLabel')}</strong>
          <p>{data.calories} kcal</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-arrow-up"></i> {t('cycling.ascentLabel')}</strong>
          <p>{data.ascent} m</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-arrow-down"></i> {t('cycling.descentLabel')}</strong>
          <p>{data.descent} m</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-thermometer-half"></i> {t('cycling.tempAvg')}</strong>
          <p>{data.temperatureAvg} °C</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-thermometer-high"></i> {t('cycling.tempMax')}</strong>
          <p>{data.temperatureMax} °C</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-beaker"></i> {t('cycling.tssLabel')}</strong>
          <p>{data.tss}</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-bolt"></i> {t('cycling.ftpLabel')}</strong>
          <p>{data.ftp} W</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-bullseye"></i> {t('cycling.thrLabel')}</strong>
          <p>{data.thr} bpm</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-heart-pulse"></i> {t('cycling.avgHRLabel')}</strong>
          <p>{data.avgHR} bpm</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-heart-fill"></i> {t('cycling.maxHRLabel')}</strong>
          <p>{data.maxHR} bpm</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-speedometer"></i> {t('cycling.avgSpeedLabel')}</strong>
          <p>{data.avgSpeed} km/h</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-speedometer2"></i> {t('cycling.maxSpeedLabel')}</strong>
          <p>{data.maxSpeed} km/h</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-arrow-repeat"></i> {t('cycling.avgCadenceLabel')}</strong>
          <p>{data.avgCadence} rpm</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-arrow-repeat"></i> {t('cycling.maxCadenceLabel')}</strong>
          <p>{data.maxCadence} rpm</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-lightning-fill"></i> {t('cycling.avgPowerLabel')}</strong>
          <p>{data.avgPower} W</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-lightning-charge-fill"></i> {t('cycling.maxPowerLabel')}</strong>
          <p>{data.maxPower} W</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-graph-up-arrow"></i> {t('cycling.npLabel')}</strong>
          <p>{data.normalizedPower} W</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-bar-chart-line"></i> {t('cycling.ifLabel')}</strong>
          <p>{data.intensityFactor}</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-wind"></i> {t('cycling.vo2maxLabel')}</strong>
          <p>{data.vo2max}</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-diagram-3"></i> {t('cycling.wkgLabel')}</strong>
          <p>{data.wKg}</p>
        </div>
        <div className="summary-item">
          <strong><i className="bi bi-battery-charging"></i> {t('cycling.totalWork')}</strong>
          <p>{data.totalWork} kJ</p>
        </div>
      </div>
    </div>
  );
};

export default CyclingSummary;
